import type { LegacyRiskGapStatus } from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { RiskGapCopyButton } from '@ctw/shared/content/risk/risk-gap-copy-button';
import { useRiskGapSummaryNoteContext } from '@ctw/shared/context/controlled-text-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faClock, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import {
  RiskGapStatusDialogContent,
  type RiskGapStatusDialogStatus,
} from './risk-gap-status-dialog';

export interface RiskGapStatusFormV2Props {
  currentStatus: LegacyRiskGapStatus;
  onSave?: (
    note: string,
    status: RiskGapStatusDialogStatus,
    substatuses?: Array<string>,
  ) => void | Promise<void>;
}

export const RiskGapStatusFormV2 = ({ currentStatus, onSave }: RiskGapStatusFormV2Props) => {
  const { controlledText: note, setControlledText: setNote } = useRiskGapSummaryNoteContext();
  const [isConfirming, setIsConfirming] = useState(false);

  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'StatusDialogModal',
    layout: { fullbleed: false, mode: 'fit', overflowBehavior: 'scroll', showCloseButton: false },
  });

  const isPendingGap = (status: LegacyRiskGapStatus) => status === 'pending';

  const handleNoteChange = (note: string) => {
    setNote(note);
  };

  const handleCancel = () => {
    setIsConfirming(false);
    closeModal();
  };

  const handleConfirmWithSubstatuses = async (
    callback:
      | ((
          notes: string,
          status: RiskGapStatusDialogStatus,
          substatuses?: Array<string>,
        ) => void | Promise<void>)
      | undefined,
    status: RiskGapStatusDialogStatus,
    substatuses: Array<string>,
    pendingNote: string,
  ) => {
    if (!callback) {
      return;
    }
    try {
      await callback(pendingNote, status, substatuses);
      closeModal();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAction = (status: RiskGapStatusDialogStatus) => {
    setIsConfirming(true);
    openModal({
      telemetry,
      action: {
        content: {
          body: (
            <RiskGapStatusDialogContent
              note={note ?? ''}
              status={status}
              onConfirm={(pendingStatus, substatuses, pendingNote) => {
                setNote(pendingNote);
                return handleConfirmWithSubstatuses(
                  onSave,
                  pendingStatus,
                  substatuses,
                  pendingNote,
                );
              }}
              onCancel={handleCancel}
            />
          ),
        },
      },
    });
  };

  return (
    <div className={tw`flex w-full flex-col gap-4 transition-all duration-100`}>
      <div className="group relative">
        <textarea
          id="risk-gap-status-note"
          placeholder="Add Note..."
          onChange={(event) => {
            if (!isConfirming) {
              handleNoteChange(event.currentTarget.value);
            }
          }}
          value={note ?? ''}
          rows={4}
          className={tw`w-full border border-meta-background shadow-none focus:outline-double focus:outline-meta-background`}
        />
        <RiskGapCopyButton
          copyContent={note ?? ''}
          buttonDisabled={!note}
          tooltipEnabled={!!note}
          tooltipContent={'Copy notes to clipboard'}
          buttonClassName={twx(tw`absolute top-2 right-2`, {
            'opacity-50': !note,
          })}
        />
      </div>
      <div className={tw`flex w-full flex-wrap justify-end gap-3`}>
        {!isPendingGap(currentStatus) ? (
          <Button
            type="button"
            size="lg"
            variant="secondary"
            className={tw`border-none bg-transparent shadow-none hover:bg-transparent`}
            onClick={() => handleAction('pending')}
          >
            <FontAwesomeIcon icon={faClock} className={tw`h-4`} /> Save as Pending
          </Button>
        ) : null}
        <Button
          type="button"
          size="lg"
          variant="secondary"
          className={tw`shadow-none`}
          onClick={() => handleAction('dismissed')}
        >
          <FontAwesomeIcon
            icon={faThumbsDown}
            className={tw`relative mr-1.5 h-4 scale-x-[-1.1] scale-y-[1.1]`}
          />
          Dismiss
        </Button>
        <Button
          type="button"
          size="lg"
          variant="secondary"
          className={tw`border-none bg-primary-main text-content-inverse shadow-none hover:bg-primary-main`}
          onClick={() => handleAction('accepted-for-review')}
        >
          <FontAwesomeIcon icon={faThumbsUp} className={tw`relative mr-1.5 h-4`} />
          Accept
        </Button>
      </div>
    </div>
  );
};
